<template>
  <OnBoardingFrame
    :as-form="isFormState"
    :back="!isCallback"
    :title="formTitle"
    :subtitle="formSubtitle"
    :error="isFormState ? errorMessage : null"
    @back="backTapped"
    @submit="onSubmit"
  >
    <!-- The two forms: the default request (ask for an email, send the link), and
         the callback opened in a different browser than the one that requested
         the link, where no email is cached and signInWithEmailLink needs it. -->
    <div v-if="isFormState" class="ob-field">
      <label class="ob-label" :for="emailInputId">{{ $t('shared.email') }}</label>
      <input
        :id="emailInputId"
        v-model.trim="email"
        type="email"
        class="ob-input"
        placeholder="example@email.com"
        autocomplete="email"
        required
      >
    </div>

    <!-- Callback: the link couldn't be verified (expired / already used /
         tampered). Offer a fresh start. -->
    <div v-else-if="isCallback && errorMessage" class="ob-center">
      <span class="ob-center__icon ob-center__icon--error"><span class="ms" aria-hidden="true">error</span></span>
      <h2 id="onBoardingTitle" class="ob-title">{{ $t('on_boarding.magic_link_page.we_couldn_t_sign_you_in') }}</h2>
      <p class="ob-center__text">{{ errorMessage }}</p>
    </div>

    <!-- Callback: verifying the link / signing the user in. -->
    <div v-else-if="isCallback" class="ob-center">
      <span class="ob-center__icon"><span class="ms fill" aria-hidden="true">auto_awesome</span></span>
      <h2 id="onBoardingTitle" class="ob-title">{{ $t('on_boarding.magic_link_page.signing_you_in_2') }}</h2>
      <p class="ob-center__text">{{ $t('on_boarding.magic_link_page.hang_tight_while_we_verify_your') }}</p>
    </div>

    <!-- Request flow confirmation. -->
    <template v-else>
      <div class="ob-center">
        <span class="ob-center__icon"><span class="ms fill" aria-hidden="true">mark_email_read</span></span>
        <h2 id="onBoardingTitle" class="ob-title">{{ $t('on_boarding.magic_link_page.check_your_inbox') }}</h2>
        <i18n-t keypath="on_boarding.magic_link_page.we_ve_sent_a_sign_in_link" tag="p" class="ob-center__text" scope="global">
          <template #email><strong>{{ submittedEmail }}</strong></template>
        </i18n-t>
      </div>
      <i18n-t keypath="on_boarding.magic_link_page.didn_t_get_it_check_your_spam" tag="p" class="ob-small" scope="global">
        <template #link>
          <button type="button" class="ob-inline-link" @click="reset">{{ $t('on_boarding.magic_link_page.try_a_different_email') }}</button>
        </template>
      </i18n-t>
    </template>

    <template v-if="isFormState || (isCallback && errorMessage)" #footer>
      <button v-if="isFormState" type="submit" class="ob-btn ob-btn-primary" :disabled="isLoading">
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ isCallback ? $t('on_boarding.magic_link_page.signing_you_in') : $t('on_boarding.magic_link_page.sending_link') }}</span>
        </template>
        <template v-else>
          {{ isCallback ? $t('on_boarding.magic_link_page.finish_sign_in') : $t('on_boarding.magic_link_page.send_sign_in_link') }}
        </template>
      </button>
      <a v-else href="/login" class="ob-btn ob-btn-secondary">
        <span class="ms" aria-hidden="true">refresh</span>
        <span>{{ $t('on_boarding.magic_link_page.request_a_new_link') }}</span>
      </a>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { auth } from '../../../utils/firebase/auth';
import { isSignInWithEmailLink } from 'firebase/auth';
import OnBoardingFrame from './OnBoardingFrame.vue';

export default defineComponent({
  name: 'OnBoardingMagicLinkPage',
  components: { OnBoardingFrame },
  data() {
    return {
      email: '',
      submittedEmail: '',
      sent: false,
      // Computed once on mount: this controls which branch the template
      // renders. We don't recompute it because the URL is cleaned up after a
      // successful sign-in (we don't want the verifying state to flip to the
      // request form just because the query params got stripped).
      isCallback: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    needsEmail(): boolean {
      return this.$store.state.onBoarding.magicLinkNeedsEmail;
    },
    // The request form, or the callback's re-enter-your-email form.
    isFormState(): boolean {
      return this.isCallback ? this.needsEmail : !this.sent;
    },
    formTitle(): string {
      if (!this.isFormState) return '';
      return this.isCallback
        ? this.$t('on_boarding.magic_link_page.confirm_your_email')
        : this.$t('on_boarding.magic_link_page.sign_in_with_a_magic_link');
    },
    formSubtitle(): string {
      if (!this.isFormState) return '';
      return this.isCallback
        ? this.$t('on_boarding.magic_link_page.it_looks_like_you_opened_the')
        : this.$t('on_boarding.magic_link_page.enter_your_email_and_we_ll');
    },
    emailInputId(): string {
      return this.isCallback ? 'magicLinkConfirmEmail' : 'magicLinkEmail';
    },
  },
  created() {
    this.isCallback = isSignInWithEmailLink(auth, window.location.href);
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
    onSubmit() {
      if (this.isCallback) this.completeWithEmail();
      else this.requestLink();
    },
    async requestLink() {
      await this.$store.dispatch('onBoarding/requestMagicLink', this.email.trim());
      if (!this.errorMessage) {
        this.submittedEmail = this.email.trim();
        this.sent = true;
      }
    },
    async completeWithEmail() {
      await this.$store.dispatch('onBoarding/completeMagicLinkSignIn', this.email.trim());
    },
    reset() {
      this.sent = false;
      this.email = '';
      this.submittedEmail = '';
      this.$store.commit('onBoarding/setError', null);
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
    this.$store.commit('onBoarding/setMagicLinkNeedsEmail', false);
  },
});
</script>
